import { RefObject, useEffect } from 'react';

interface AutoScrollOptions {
	enabled: boolean;
	/** Scroll speed in pixels per second. */
	speed?: number;
	/** How long to hold at the top and bottom of the list before moving on. */
	edgePauseMs?: number;
	/** How long to wait after the user stops interacting before auto-scroll resumes. */
	resumeDelayMs?: number;
}

type PauseReason = 'hover' | 'focus' | 'manual' | 'offscreen' | 'hidden';
type Phase = 'holdTop' | 'scrolling' | 'holdBottom' | 'returning';

// Caps the per-frame step so a long frame gap (e.g. tab switch) never jumps past content.
const MAX_FRAME_MS = 100;

/**
 * Slowly scrolls an overflowing container from top to bottom, holds, glides back to the
 * top and repeats. Pauses while the user hovers, focuses, scrolls or touches the list,
 * and while the container is off screen or the tab is hidden.
 */
export const useAutoScroll = (
	ref: RefObject<HTMLElement>,
	{ enabled, speed = 20, edgePauseMs = 2500, resumeDelayMs = 3000 }: AutoScrollOptions,
) => {
	useEffect(() => {
		const el = ref.current;
		if (!enabled || !el) return;
		if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

		const pauseReasons = new Set<PauseReason>();
		const resumeTimers: Partial<Record<PauseReason, ReturnType<typeof setTimeout>>> = {};
		let phase: Phase = 'holdTop';
		let holdUntil: number | null = null;
		let position = el.scrollTop;
		let lastTs: number | null = null;
		let frameId = 0;

		const pause = (reason: PauseReason) => {
			clearTimeout(resumeTimers[reason]);
			pauseReasons.add(reason);
		};

		const resume = (reason: PauseReason) => {
			clearTimeout(resumeTimers[reason]);
			pauseReasons.delete(reason);
		};

		const resumeLater = (reason: PauseReason) => {
			clearTimeout(resumeTimers[reason]);
			resumeTimers[reason] = setTimeout(() => resume(reason), resumeDelayMs);
		};

		const tick = (ts: number) => {
			frameId = requestAnimationFrame(tick);
			const dt = lastTs === null ? 0 : Math.min(ts - lastTs, MAX_FRAME_MS);
			lastTs = ts;

			const maxScroll = el.scrollHeight - el.clientHeight;
			if (pauseReasons.size > 0 || maxScroll <= 0) {
				// Pick up from wherever the user left the list once we resume.
				position = el.scrollTop;
				phase = 'scrolling';
				holdUntil = null;
				return;
			}

			switch (phase) {
				case 'holdTop':
				case 'holdBottom':
					if (holdUntil === null) holdUntil = ts + edgePauseMs;
					if (ts < holdUntil) return;
					holdUntil = null;
					if (phase === 'holdTop') {
						phase = 'scrolling';
					} else {
						phase = 'returning';
						if (typeof el.scrollTo === 'function') {
							el.scrollTo({ top: 0, behavior: 'smooth' });
						} else {
							el.scrollTop = 0;
						}
					}
					return;
				case 'returning':
					position = el.scrollTop;
					if (position <= 1) phase = 'holdTop';
					return;
				case 'scrolling':
					// Something else moved the list (e.g. keyboard scrolling); follow it.
					if (Math.abs(el.scrollTop - position) > 2) position = el.scrollTop;
					position = Math.min(position + (speed * dt) / 1000, maxScroll);
					el.scrollTop = position;
					if (position >= maxScroll - 1) phase = 'holdBottom';
			}
		};

		const onPointerEnter = (e: PointerEvent) => {
			if (e.pointerType === 'mouse') pause('hover');
		};
		const onPointerLeave = (e: PointerEvent) => {
			if (e.pointerType === 'mouse') resumeLater('hover');
		};
		const onFocusIn = () => pause('focus');
		const onFocusOut = (e: FocusEvent) => {
			if (!el.contains(e.relatedTarget as Node | null)) resumeLater('focus');
		};
		const onManualInteraction = () => {
			pause('manual');
			resumeLater('manual');
		};
		const onVisibilityChange = () => {
			if (document.visibilityState === 'hidden') pause('hidden');
			else resume('hidden');
		};

		const manualEvents = ['wheel', 'touchstart', 'touchmove', 'pointerdown'] as const;
		el.addEventListener('pointerenter', onPointerEnter);
		el.addEventListener('pointerleave', onPointerLeave);
		el.addEventListener('focusin', onFocusIn);
		el.addEventListener('focusout', onFocusOut);
		manualEvents.forEach((type) => el.addEventListener(type, onManualInteraction, { passive: true }));
		document.addEventListener('visibilitychange', onVisibilityChange);

		let observer: IntersectionObserver | undefined;
		if (typeof IntersectionObserver !== 'undefined') {
			observer = new IntersectionObserver(([entry]) => {
				if (entry?.isIntersecting) resume('offscreen');
				else pause('offscreen');
			});
			observer.observe(el);
		}

		frameId = requestAnimationFrame(tick);

		return () => {
			cancelAnimationFrame(frameId);
			Object.values(resumeTimers).forEach((timer) => clearTimeout(timer));
			el.removeEventListener('pointerenter', onPointerEnter);
			el.removeEventListener('pointerleave', onPointerLeave);
			el.removeEventListener('focusin', onFocusIn);
			el.removeEventListener('focusout', onFocusOut);
			manualEvents.forEach((type) => el.removeEventListener(type, onManualInteraction));
			document.removeEventListener('visibilitychange', onVisibilityChange);
			observer?.disconnect();
		};
	}, [ref, enabled, speed, edgePauseMs, resumeDelayMs]);
};
